import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { ContentState, EditorState, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import Redactor from './redactor-component';

/**
 * @param {object} root0 - The root object containing the component props.
 * @param {Function} root0.onChange - The function to be called when the editor state changes.
 * @param {string} root0.value - The initial value of the editor.
 * @param {boolean} root0.view - Whether the editor is in view mode or not.
 * @param {string} root0.header - The header text to be displayed.
 * @param {string} root0.placeholder - The placeholder text to be displayed.
 * @param {string} root0.cy - The Cypress selector for testing.
 * @param {boolean} root0.newVersion - Whether the editor is in new version or not.
 * @param {Function} root0.onBlur - The function to call when the editor loses focus.
 * @returns {JSX.Element} - The rendered component.
 */
const ControlledRedactor = ({
  onChange,
  value,
  view,
  header,
  placeholder,
  cy,
  newVersion,
  onBlur,
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [updated, setUpdated] = useState(false);
  const [lastExternalValue, setLastExternalValue] = useState('');

  useEffect(() => {
    const defaultValue = value || '';

    // Handle the case where the value is changed externally example revert in the option form
    // Only update if this is the initial load or if the value changed externally
    if (!updated || (updated && defaultValue !== lastExternalValue)) {
      const blocksFromHtml = convertFromHTML(defaultValue);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHtml.contentBlocks,
        blocksFromHtml.entityMap,
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setEditorState(newEditorState);
      setLastExternalValue(defaultValue);
    }
  }, [value, updated, lastExternalValue]);

  const onEditorStateChange = (newEditorState) => {
    setUpdated(true);
    setEditorState(newEditorState);

    // Update the external value tracker to current content
    const currentContent = stateToHTML(newEditorState.getCurrentContent());
    setLastExternalValue(currentContent);

    return onChange(currentContent);
  };

  return (
    <Redactor
      placeholder={placeholder || 'Type your description here...'}
      header={header}
      fullScreen
      value={editorState}
      border
      disabled={view}
      onChange={onEditorStateChange}
      cy={cy}
      newVersion={newVersion}
      onBlur={onBlur}
    />
  );
};

ControlledRedactor.defaultProps = {
  view: false,
  header: undefined,
  placeholder: undefined,
  cy: undefined,
  newVersion: false,
  onBlur: () => {},
};

ControlledRedactor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  view: PropTypes.bool,
  header: PropTypes.string,
  cy: PropTypes.string,
  placeholder: PropTypes.string,
  newVersion: PropTypes.bool,
  onBlur: PropTypes.func,
};

export default ControlledRedactor;
